import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Row, Col, Card, Form, Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { showCompanyTaskRequest, updateCompanyTaskRequest } from '../../../requests/companyTasks';
import { Icon, DefaultModal, FormikInput, ComponentDataTable, DatatableEmployeeName } from '../../../components';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';
import { sendAlert } from '../../../actions/utils';
import basicTask from '../../Shared/CompanyTask/task';

const employeeColumns = [
  {
    name: 'NOMBRE',
    selector: 'fullName',
    cell: item => <DatatableEmployeeName item={item} fileName="fileInfo" name="fullName" fileUrl="fileUrl" />,
    grow: '3',
    sortable: true
  }
];

const BossTaskShow = ({ taskId, handleBack }) => {
  const dispatch = useDispatch();

  const [task, setTask] = useState(camelCaseEmptyStringRecursive(basicTask));
  const [loading, setLoading] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState('');

  const fetchTask = useCallback(() => {
    if (!taskId) return;
    setLoading(true);
    showCompanyTaskRequest(taskId, {
      dispatch,
      successCallback: response => {
        setTask(camelCaseEmptyStringRecursive(response.data));
        setStatus(response.data.status);
        setLoading(false);
      },
      failureCallback: () => {
        setLoading(false);
        handleBack();
        dispatch(sendAlert({ kind: 'error', message: 'Tarea no encontrada' }));
      }
    });
  }, [dispatch, handleBack, taskId]);

  const handleUpdateStatus = () => {
    setLoading(true);
    const params = {
      company_task: {
        status
      }
    };
    updateCompanyTaskRequest(taskId, {
      dispatch,
      params,
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Estado actualizado con éxito' }));
        fetchTask();
      },
      failureCallback: () => setLoading(false)
    });
  };

  const handleAddComment = () => {
    if (!comment) return;
    setLoading(true);
    const params = {
      company_task: {
        company_task_comments_attributes: [{ comment }]
      }
    };
    updateCompanyTaskRequest(taskId, {
      dispatch,
      params,
      successCallback: () => {
        dispatch(sendAlert({ kind: 'success', message: 'Comentario agregado con éxito' }));
        setModalShow(false);
        setComment('');
        fetchTask();
      },
      failureCallback: () => setLoading(false)
    });
  };

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  if (loading && !task.id) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '30vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const renderAttribute = (label, value, md = 3) => (
    <Col md={md} className="mb-3">
      <div className="d-flex flex-column">
        <span className="type">{label}</span>
        <span className={`answer ${!value ? 'text-grey' : ''}`}>{value || 'No Aplica'}</span>
      </div>
    </Col>
  );

  return (
    <>
      <Row className="px-3 mt-2 mb-2 align-items-center">
        <Col xs="auto">
          <Button variant="circle-dark" className="btn-circle" onClick={handleBack}>
            <Icon icon="chevron-back" />
          </Button>
        </Col>
        <Col>
          <h2 className="text-uppercase mb-0">Detalle de la Tarea</h2>
        </Col>
      </Row>

      <Card className="mb-4">
        <Col className="top-header-green">
          <div className="card-header-title">Datos Generales</div>
        </Col>
        <Card.Body>
          <Row>
            {renderAttribute('Título', task.title)}
            {renderAttribute('Desde', task.startDate)}
            {renderAttribute('Hasta', task.endDate)}
            {renderAttribute('Prioridad', task.translatedPriority)}
          </Row>
          <Row className="align-items-center">
            <Col md={3} className="mb-3">
              <Form.Group className="mb-0">
                <Form.Label className="type">Estado:</Form.Label>
                <Form.Control
                  as="select"
                  value={status}
                  onChange={e => setStatus(e.target.value)}
                  className="custom-select"
                  disabled={task.status === 'finished'}
                >
                  <option value="pending">Pendiente</option>
                  <option value="finished">Terminada</option>
                </Form.Control>
              </Form.Group>
            </Col>
            {renderAttribute('Enviar Mail', task.sendEmail ? 'Si' : 'No')}
            {renderAttribute('Enviar Notificación', task.sendNotification ? 'Si' : 'No')}
          </Row>
          {task.description && <Row>{renderAttribute('Descripción', task.description, 12)}</Row>}

          <Row className="justify-content-end mt-2">
            <Col xs="auto">
              <Button
                variant="primary"
                onClick={handleUpdateStatus}
                disabled={loading || status === task.status || task.status === 'finished'}
                className="text-uppercase font-weight-bold px-4"
              >
                GUARDAR
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Col className="top-header-green">
          <div className="card-header-title">Comentarios</div>
        </Col>
        <Card.Body>
          {task.companyTaskComments?.length > 0 ? (
            <div className="mb-4">
              {task.companyTaskComments.map((taskComment, index) => (
                <div key={taskComment.id} className="mb-3">
                  <p className="mb-1">
                    <span className="font-weight-bold">{taskComment.employeeName}:</span>
                    <span className="ml-1">{taskComment.comment}</span>
                  </p>
                  <p className="text-muted small mb-2">{taskComment.createdAt}</p>
                  {index !== task.companyTaskComments.length - 1 && <hr className="my-2 border-light" />}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted mb-4">No hay comentarios aún.</p>
          )}

          <Row className="justify-content-end">
            <Col xs="auto">
              <Button variant="primary" onClick={() => setModalShow(true)} className="text-uppercase font-weight-bold">
                AGREGAR COMENTARIO
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Col className="top-header-green">
          <div className="card-header-title">Colaborador Asignado</div>
        </Col>
        <Card.Body className="p-0">
          <div className="data-table-yellow">
            <ComponentDataTable
              columns={employeeColumns}
              data={task.employees || []}
              totalRows={task.employees?.length || 0}
              withDateSelect={false}
              withSearch={false}
              withPagination={false}
              onRequest={false}
            />
          </div>
        </Card.Body>
      </Card>

      <DefaultModal
        title="AGREGAR COMENTARIO"
        body={
          <FormikInput
            as="textarea"
            label="Comentario"
            maxLength={300}
            value={comment}
            onChange={e => setComment(e.target.value)}
          />
        }
        show={modalShow}
        handleClose={() => setModalShow(false)}
        handleConfirm={handleAddComment}
        titleBtnSave="AGREGAR COMENTARIO"
        variantBtnSave="primary"
        withConfirm
        disabled={loading || !comment}
      />
    </>
  );
};

export default BossTaskShow;
